import React, { useEffect, useState } from 'react';
import { PackageIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, UserIcon, TagIcon, DollarSignIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { Product, ProductSale, ProductSaleItem } from '../../types';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { supabase } from '../../utils/supabase';
// Helper to normalize names for comparison
function normalizeName(name: string) {
  if (!name) return '';
  return name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^(dr\.|dra\.|lic\.|ing\.|ter\.)\s*/g, '').replace(/\s+/g, ' ');
}
const CATEGORIES = [{
  value: 'Cuidado Facial',
  label: 'Cuidado Facial'
}, {
  value: 'Cuidado Corporal',
  label: 'Cuidado Corporal'
}, {
  value: 'Suplementos',
  label: 'Suplementos'
}, {
  value: 'Equipos',
  label: 'Equipos'
}, {
  value: 'Accesorios',
  label: 'Accesorios'
}, {
  value: 'Otro',
  label: 'Otro'
}];
// ─── helpers: Supabase row → app type ────────────────────────────────────────
function rowToProduct(row: any): Product {
  return {
    id: row.id,
    name: row.name,
    sku: row.sku,
    description: row.description ?? '',
    price: Number(row.price),
    cost: Number(row.cost),
    stock: row.stock,
    category: row.category,
    status: row.status,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at)
  };
}
function rowToSale(row: any): ProductSale {
  return {
    id: row.id,
    date: row.date,
    patientId: row.patient_id ?? '',
    items: row.items as ProductSaleItem[],
    total: Number(row.total),
    notes: row.notes ?? '',
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    createdAt: new Date(row.created_at)
  };
}
export function Products() {
  const {
    patients,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [sellers, setSellers] = useState<{
    id: string;
    name: string;
  }[]>([]);
  // ─── Load Data from Supabase ─────────────────────────────────────────────
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [{
          data: productsData,
          error: productsError
        }, {
          data: salesData,
          error: salesError
        }, {
          data: usersData
        }, {
          data: doctorsData
        }] = await Promise.all([supabase.from('products').select('*').order('created_at', {
          ascending: false
        }), supabase.from('product_sales').select('*').order('date', {
          ascending: false
        }), supabase.from('users').select('id, name, role'), supabase.from('doctors').select('id, name')]);
        if (productsError) throw productsError;
        if (salesError) throw salesError;
        setProducts((productsData ?? []).map(rowToProduct));
        setSales((salesData ?? []).map(rowToSale));
        // Build sellers list: users with allowed roles + doctors, deduplicated by name
        const allowedRoles = new Set(['doctor', 'doctora', 'terapeuta', 'terapista', 'recepcion', 'recepción', 'recepcionista', 'callcenter', 'call center', 'call_center']);
        const sellersList: {
          id: string;
          name: string;
        }[] = [];
        const seenNormalizedNames = new Set<string>();
        (usersData ?? []).forEach((u: any) => {
          if (!allowedRoles.has((u.role || '').toLowerCase())) return;
          const norm = normalizeName(u.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: u.id,
              name: u.name
            });
          }
        });
        (doctorsData ?? []).forEach((d: any) => {
          const norm = normalizeName(d.name);
          if (norm && !seenNormalizedNames.has(norm)) {
            seenNormalizedNames.add(norm);
            sellersList.push({
              id: d.id,
              name: d.name
            });
          }
        });
        setSellers(sellersList);
      } catch (e) {
        console.error('Error loading data from Supabase:', e);
        addNotification({
          type: 'error',
          title: 'Error de carga',
          message: 'No se pudieron cargar los productos desde la base de datos.'
        });
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);
  // ─── CATALOG TAB STATE ───────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    category: 'Cuidado Facial',
    status: 'active' as 'active' | 'inactive'
  });
  const [productErrors, setProductErrors] = useState<Record<string, string>>({});
  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase()) || p.category.toLowerCase().includes(productSearch.toLowerCase()));
  const {
    sortedItems: sortedProducts,
    requestSort: requestProductSort,
    isSorted: isProductSorted
  } = useSortableData(filteredProducts);
  const {
    paginatedItems: paginatedProducts,
    currentPage: productPage,
    totalPages: productTotalPages,
    totalItems: productTotalItems,
    startIndex: productStartIndex,
    endIndex: productEndIndex,
    goToPage: productGoToPage,
    nextPage: productNextPage,
    prevPage: productPrevPage
  } = usePagination(sortedProducts);
  const handleOpenProductModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        sku: product.sku,
        description: product.description,
        price: product.price.toString(),
        cost: product.cost.toString(),
        stock: product.stock.toString(),
        category: product.category,
        status: product.status
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        sku: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        category: 'Cuidado Facial',
        status: 'active'
      });
    }
    setProductErrors({});
    setIsProductModalOpen(true);
  };
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!productForm.name.trim()) errors.name = 'El nombre es requerido';
    if (!productForm.sku.trim()) errors.sku = 'El SKU es requerido';
    if (!productForm.price || isNaN(Number(productForm.price))) errors.price = 'Precio inválido';
    if (!productForm.cost || isNaN(Number(productForm.cost))) errors.cost = 'Costo inválido';
    if (!productForm.stock || isNaN(Number(productForm.stock))) errors.stock = 'Stock inválido';
    if (Object.keys(errors).length > 0) {
      setProductErrors(errors);
      return;
    }
    const payload = {
      name: productForm.name,
      sku: productForm.sku,
      description: productForm.description,
      price: Number(productForm.price),
      cost: Number(productForm.cost),
      stock: Number(productForm.stock),
      category: productForm.category,
      status: productForm.status
    };
    try {
      if (editingProduct) {
        const {
          data,
          error
        } = await supabase.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (error) throw error;
        setProducts(products.map(p => p.id === editingProduct.id ? rowToProduct(data) : p));
        addNotification({
          type: 'success',
          title: 'Producto Actualizado',
          message: `${productForm.name} ha sido actualizado.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'update',
          module: 'products',
          entityId: editingProduct.id,
          entityName: productForm.name,
          details: `Producto actualizado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      } else {
        const {
          data,
          error
        } = await supabase.from('products').insert(payload).select().single();
        if (error) throw error;
        setProducts([rowToProduct(data), ...products]);
        addNotification({
          type: 'success',
          title: 'Producto Creado',
          message: `${productForm.name} ha sido agregado al catálogo.`
        });
        addAuditLog({
          userId: user?.id || 'system',
          userName: user?.name || 'Sistema',
          action: 'create',
          module: 'products',
          entityId: data.id,
          entityName: productForm.name,
          details: `Producto creado: ${productForm.name} (SKU: ${productForm.sku}) - Precio: ${formatCurrency(Number(productForm.price))}, Stock: ${productForm.stock}`
        });
      }
      setIsProductModalOpen(false);
    } catch (e: any) {
      console.error('Error saving product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo guardar el producto.'
      });
    }
  };
  const handleDeleteProduct = async (id: string) => {
    if (!confirm('¿Está seguro de que desea eliminar este producto?')) return;
    try {
      const deletedProduct = products.find(p => p.id === id);
      const {
        error
      } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
      setProducts(products.filter(p => p.id !== id));
      addNotification({
        type: 'success',
        title: 'Producto Eliminado',
        message: 'El producto ha sido eliminado del catálogo.'
      });
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'delete',
        module: 'products',
        entityId: id,
        entityName: deletedProduct?.name || 'Producto',
        details: `Producto eliminado: ${deletedProduct?.name || 'Producto'} (SKU: ${deletedProduct?.sku || 'N/A'})`
      });
    } catch (e: any) {
      console.error('Error deleting product:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo eliminar el producto.'
      });
    }
  };
  // ─── SALES TAB STATE ─────────────────────────────────────────────────────
  const [saleSearch, setSaleSearch] = useState('');
  const [saleDateFrom, setSaleDateFrom] = useState('');
  const [saleDateTo, setSaleDateTo] = useState('');
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({
    patientId: '',
    sellerId: '',
    date: getToday(),
    notes: ''
  });
  const [saleItems, setSaleItems] = useState<{
    productId: string;
    quantity: number;
  }[]>([]);
  const [saleErrors, setSaleErrors] = useState<Record<string, string>>({});
  const filteredSales = sales.filter(s => {
    const patient = patients.find(p => p.id === s.patientId);
    const matchesSearch = !saleSearch || patient?.name.toLowerCase().includes(saleSearch.toLowerCase()) || s.createdByUserName.toLowerCase().includes(saleSearch.toLowerCase());
    const matchesDateFrom = !saleDateFrom || s.date >= saleDateFrom;
    const matchesDateTo = !saleDateTo || s.date <= saleDateTo;
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });
  const {
    sortedItems: sortedSales,
    requestSort: requestSaleSort,
    isSorted: isSaleSorted
  } = useSortableData(filteredSales);
  const {
    paginatedItems: paginatedSales,
    currentPage: salePage,
    totalPages: saleTotalPages,
    totalItems: saleTotalItems,
    startIndex: saleStartIndex,
    endIndex: saleEndIndex,
    goToPage: saleGoToPage,
    nextPage: saleNextPage,
    prevPage: salePrevPage
  } = usePagination(sortedSales);
  const handleOpenSaleModal = () => {
    setSaleForm({
      patientId: '',
      sellerId: '',
      date: getToday(),
      notes: ''
    });
    setSaleItems([{
      productId: '',
      quantity: 1
    }]);
    setSaleErrors({});
    setIsSaleModalOpen(true);
  };
  const addSaleItem = () => setSaleItems([...saleItems, {
    productId: '',
    quantity: 1
  }]);
  const removeSaleItem = (index: number) => setSaleItems(saleItems.filter((_, i) => i !== index));
  const updateSaleItem = (index: number, field: 'productId' | 'quantity', value: any) => {
    const newItems = [...saleItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    setSaleItems(newItems);
  };
  const calculateSaleTotal = () => saleItems.reduce((total, item) => {
    if (!item.productId) return total;
    const product = products.find(p => p.id === item.productId);
    if (!product) return total;
    return total + product.price * (item.quantity || 0);
  }, 0);
  const handleSaveSale = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!saleForm.patientId) errors.patientId = 'Debe seleccionar un cliente/paciente';
    if (!saleForm.sellerId) errors.sellerId = 'Debe seleccionar un vendedor';
    if (!saleForm.date) errors.date = 'La fecha es requerida';
    const validItems = saleItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) errors.items = 'Debe agregar al menos un producto válido';
    validItems.forEach((item, idx) => {
      const product = products.find(p => p.id === item.productId);
      if (product && product.stock < item.quantity) errors[`item_${idx}`] = `Stock insuficiente (${product.stock} disponibles)`;
    });
    if (Object.keys(errors).length > 0) {
      setSaleErrors(errors);
      return;
    }
    const finalItems: ProductSaleItem[] = validItems.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      return {
        productId: item.productId,
        productName: product.name,
        quantity: item.quantity,
        unitPrice: product.price,
        subtotal: product.price * item.quantity
      };
    });
    const total = finalItems.reduce((sum, item) => sum + item.subtotal, 0);
    // Use selected seller instead of logged-in user
    const selectedSeller = sellers.find(s => s.id === saleForm.sellerId);
    const sellerUserId = selectedSeller?.id ?? user?.id ?? 'unknown';
    const sellerUserName = selectedSeller?.name ?? user?.name ?? 'Usuario';
    try {
      // Insert sale
      const {
        data: saleData,
        error: saleError
      } = await supabase.from('product_sales').insert({
        date: saleForm.date,
        patient_id: saleForm.patientId || null,
        items: finalItems,
        total,
        notes: saleForm.notes,
        created_by_user_id: sellerUserId,
        created_by_user_name: sellerUserName
      }).select().single();
      if (saleError) throw saleError;
      // Update stock for each product sold
      await Promise.all(finalItems.map(item => {
        const product = products.find(p => p.id === item.productId)!;
        return supabase.from('products').update({
          stock: product.stock - item.quantity
        }).eq('id', item.productId);
      }));
      // Update local state
      setSales([rowToSale(saleData), ...sales]);
      setProducts(products.map(p => {
        const soldItem = finalItems.find(item => item.productId === p.id);
        return soldItem ? {
          ...p,
          stock: p.stock - soldItem.quantity
        } : p;
      }));
      addNotification({
        type: 'success',
        title: 'Venta Registrada',
        message: `Venta por ${formatCurrency(total)} registrada exitosamente.`
      });
      const patientName = patients.find(p => p.id === saleForm.patientId)?.name || 'Cliente';
      const itemsSummary = finalItems.map(item => `${item.quantity}x ${item.productName}`).join(', ');
      addAuditLog({
        userId: user?.id || 'system',
        userName: user?.name || 'Sistema',
        action: 'create',
        module: 'product_sales',
        entityId: saleData.id,
        entityName: `Venta a ${patientName}`,
        details: `Venta registrada por ${sellerUserName}: ${itemsSummary} - Total: ${formatCurrency(total)} - Cliente: ${patientName}`
      });
      setIsSaleModalOpen(false);
    } catch (e: any) {
      console.error('Error saving sale:', e);
      addNotification({
        type: 'error',
        title: 'Error',
        message: e.message ?? 'No se pudo registrar la venta.'
      });
    }
  };
  const tabs = [{
    id: 'catalog',
    label: 'Catálogo de Productos',
    icon: <PackageIcon className="w-4 h-4" data-id="element-3138" />
  }, {
    id: 'sales',
    label: 'Ventas de Productos',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3139" />
  }];
  return <div className="space-y-6" data-id="element-3140">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-orange-500 to-rose-500 p-6 text-white shadow-xl" data-id="element-3141">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3142" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3143">
          <div data-id="element-3144">
            <div className="flex items-center gap-3 mb-2" data-id="element-3145">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3146">
                <PackageIcon className="w-6 h-6" data-id="element-3147" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3148">Productos</h1>
            </div>
            <p className="text-orange-100 max-w-xl" data-id="element-3149">
              Gestión de inventario y ventas de productos
            </p>
          </div>
          <div className="flex gap-3" data-id="element-3150">
            {activeTab === 'catalog' ? <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3152" />} onClick={() => handleOpenProductModal()} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3151">
                Nuevo Producto
              </Button> : <Button leftIcon={<ShoppingCartIcon className="w-4 h-4" data-id="element-3154" />} onClick={handleOpenSaleModal} className="bg-white text-orange-700 hover:bg-orange-50 shadow-lg shadow-orange-900/20" data-id="element-3153">
                Registrar Venta
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3155" />

      {/* CATALOG TAB */}
      {activeTab === 'catalog' && <Card padding="none" data-id="element-3156">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-3157">
            <div className="relative max-w-md" data-id="element-3158">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3159" />
              <Input placeholder="Buscar por nombre, SKU o categoría..." value={productSearch} onChange={e => setProductSearch(e.target.value)} className="pl-10" data-id="element-3160" />
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-3161">
            <table className="w-full" data-id="element-3162">
              <thead data-id="element-3163">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3164">
                  <SortableHeader label="Producto" sortKey="name" currentDirection={isProductSorted('name')} onSort={requestProductSort} data-id="element-3165" />
                  <SortableHeader label="SKU" sortKey="sku" currentDirection={isProductSorted('sku')} onSort={requestProductSort} data-id="element-3166" />
                  <SortableHeader label="Categoría" sortKey="category" currentDirection={isProductSorted('category')} onSort={requestProductSort} data-id="element-3167" />
                  <SortableHeader label="Precio" sortKey="price" currentDirection={isProductSorted('price')} onSort={requestProductSort} data-id="element-3168" />
                  <SortableHeader label="Stock" sortKey="stock" currentDirection={isProductSorted('stock')} onSort={requestProductSort} data-id="element-3169" />
                  <SortableHeader label="Estado" sortKey="status" currentDirection={isProductSorted('status')} onSort={requestProductSort} data-id="element-3170" />
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3171">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3172">
                {loading ? <tr data-id="element-3173">
                    <td colSpan={7} className="py-8 text-center text-gray-400" data-id="element-3174">
                      Cargando productos...
                    </td>
                  </tr> : paginatedProducts.length === 0 ? <tr data-id="element-3175">
                    <td colSpan={7} className="py-8 text-center text-gray-500" data-id="element-3176">
                      No se encontraron productos
                    </td>
                  </tr> : paginatedProducts.map(product => <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3177">
                      <td className="py-3 px-4" data-id="element-3178">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-3179">
                          {product.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-3180">
                          {product.description}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300" data-id="element-3181">
                        {product.sku}
                      </td>
                      <td className="py-3 px-4" data-id="element-3182">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-3183">
                          <TagIcon className="w-3 h-3" data-id="element-3184" />
                          {product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3185">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-4" data-id="element-3186">
                        <span className={`font-medium ${product.stock <= 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} data-id="element-3187">
                          {product.stock}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-3188">
                        <Badge variant={product.status === 'active' ? 'success' : 'default'} data-id="element-3189">
                          {product.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-3190">
                        <div className="flex justify-end gap-2" data-id="element-3191">
                          <button onClick={() => handleOpenProductModal(product)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-3192">
                            <EditIcon className="w-4 h-4" data-id="element-3193" />
                          </button>
                          <button onClick={() => handleDeleteProduct(product.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-3194">
                            <TrashIcon className="w-4 h-4" data-id="element-3195" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3196">
            <Pagination currentPage={productPage} totalPages={productTotalPages} totalItems={productTotalItems} startIndex={productStartIndex} endIndex={productEndIndex} onPageChange={productGoToPage} onNext={productNextPage} onPrev={productPrevPage} data-id="element-3197" />
          </div>
        </Card>}

      {/* SALES TAB */}
      {activeTab === 'sales' && <Card padding="none" data-id="element-3198">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row gap-4" data-id="element-3199">
            <div className="relative flex-1" data-id="element-3200">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3201" />
              <Input placeholder="Buscar por cliente o vendedor..." value={saleSearch} onChange={e => setSaleSearch(e.target.value)} className="pl-10" data-id="element-3202" />
            </div>
            <div className="flex items-center gap-2" data-id="element-3203">
              <Input type="date" value={saleDateFrom} onChange={e => setSaleDateFrom(e.target.value)} data-id="element-3204" />
              <span className="text-gray-400" data-id="element-3205">→</span>
              <Input type="date" value={saleDateTo} onChange={e => setSaleDateTo(e.target.value)} data-id="element-3206" />
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-3207">
            <table className="w-full" data-id="element-3208">
              <thead data-id="element-3209">
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-3210">
                  <SortableHeader label="Fecha" sortKey="date" currentDirection={isSaleSorted('date')} onSort={requestSaleSort} data-id="element-3211" />
                  <SortableHeader label="Cliente/Paciente" sortKey="patientId" currentDirection={isSaleSorted('patientId')} onSort={requestSaleSort} data-id="element-3212" />
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3213">
                    Productos
                  </th>
                  <SortableHeader label="Total" sortKey="total" currentDirection={isSaleSorted('total')} onSort={requestSaleSort} data-id="element-3214" />
                  <SortableHeader label="Vendedor" sortKey="createdByUserName" currentDirection={isSaleSorted('createdByUserName')} onSort={requestSaleSort} data-id="element-3215" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3216">
                {loading ? <tr data-id="element-3217">
                    <td colSpan={5} className="py-8 text-center text-gray-400" data-id="element-3218">
                      Cargando ventas...
                    </td>
                  </tr> : paginatedSales.length === 0 ? <tr data-id="element-3219">
                    <td colSpan={5} className="py-8 text-center text-gray-500" data-id="element-3220">
                      No se encontraron ventas
                    </td>
                  </tr> : paginatedSales.map(sale => {
              const patient = patients.find(p => p.id === sale.patientId);
              return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-3221">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white" data-id="element-3222">
                          {sale.date}
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 dark:text-white" data-id="element-3223">
                          {patient?.name || 'Desconocido'}
                        </td>
                        <td className="py-3 px-4" data-id="element-3224">
                          <div className="flex flex-col gap-1" data-id="element-3225">
                            {sale.items.map((item, idx) => {
                      const product = products.find(p => p.id === item.productId);
                      return <span key={idx} className="text-xs text-gray-600 dark:text-gray-300" data-id="element-3226">
                                  {item.quantity}x{' '}
                                  {product?.name || item.productName || 'Producto eliminado'}
                                </span>;
                    })}
                          </div>
                        </td>
                        <td className="py-3 px-4 font-bold text-orange-600 dark:text-orange-400" data-id="element-3227">
                          {formatCurrency(sale.total)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 flex items-center gap-1.5 mt-1" data-id="element-3228">
                          <UserIcon className="w-3.5 h-3.5" data-id="element-3229" />
                          {sale.createdByUserName}
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-200 dark:border-gray-700" data-id="element-3230">
            <Pagination currentPage={salePage} totalPages={saleTotalPages} totalItems={saleTotalItems} startIndex={saleStartIndex} endIndex={saleEndIndex} onPageChange={saleGoToPage} onNext={saleNextPage} onPrev={salePrevPage} data-id="element-3231" />
          </div>
        </Card>}

      {/* Product Modal */}
      <Modal isOpen={isProductModalOpen} onClose={() => setIsProductModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" icon={<PackageIcon className="w-6 h-6" data-id="element-3233" />} accentColor="amber" data-id="element-3232">
        <form onSubmit={handleSaveProduct} className="space-y-6" data-id="element-3234">
          <FormSection title="Información Básica" data-id="element-3235">
            <div className="space-y-4" data-id="element-3236">
              <Input label="Nombre del Producto" value={productForm.name} onChange={e => setProductForm({
              ...productForm,
              name: e.target.value
            })} error={productErrors.name} data-id="element-3237" />
              <div className="grid grid-cols-2 gap-4" data-id="element-3238">
                <Input label="SKU (Código)" value={productForm.sku} onChange={e => setProductForm({
                ...productForm,
                sku: e.target.value
              })} error={productErrors.sku} data-id="element-3239" />
                <Select label="Categoría" options={CATEGORIES} value={productForm.category} onChange={v => setProductForm({
                ...productForm,
                category: v
              })} data-id="element-3240" />
              </div>
              <Textarea label="Descripción" value={productForm.description} onChange={e => setProductForm({
              ...productForm,
              description: e.target.value
            })} rows={2} data-id="element-3241" />
            </div>
          </FormSection>

          <FormSection title="Inventario y Precios" data-id="element-3242">
            <div className="grid grid-cols-3 gap-4" data-id="element-3243">
              <Input type="number" label="Costo" value={productForm.cost} onChange={e => setProductForm({
              ...productForm,
              cost: e.target.value
            })} error={productErrors.cost} data-id="element-3244" />
              <Input type="number" label="Precio Venta" value={productForm.price} onChange={e => setProductForm({
              ...productForm,
              price: e.target.value
            })} error={productErrors.price} data-id="element-3245" />
              <Input type="number" label="Stock" value={productForm.stock} onChange={e => setProductForm({
              ...productForm,
              stock: e.target.value
            })} error={productErrors.stock} data-id="element-3246" />
            </div>
            <div className="mt-4" data-id="element-3247">
              <Select label="Estado" options={[{
              value: 'active',
              label: 'Activo'
            }, {
              value: 'inactive',
              label: 'Inactivo'
            }]} value={productForm.status} onChange={v => setProductForm({
              ...productForm,
              status: v as any
            })} data-id="element-3248" />
            </div>
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3249">
            <Button variant="outline" type="button" onClick={() => setIsProductModalOpen(false)} data-id="element-3250">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3251">
              {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Sale Modal */}
      <Modal isOpen={isSaleModalOpen} onClose={() => setIsSaleModalOpen(false)} title="Registrar Venta" size="lg" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3253" />} accentColor="amber" data-id="element-3252">
        <form onSubmit={handleSaveSale} className="space-y-6" data-id="element-3254">
          <FormSection title="Detalles de la Venta" data-id="element-3255">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3256">
              <Select label="Cliente / Paciente" options={patients.map(p => ({
              value: p.id,
              label: p.name
            }))} value={saleForm.patientId} onChange={v => setSaleForm({
              ...saleForm,
              patientId: v
            })} error={saleErrors.patientId} searchable data-id="element-3257" />
              <Select label="Vendedor" options={sellers.map(s => ({
              value: s.id,
              label: s.name
            }))} value={saleForm.sellerId} onChange={v => setSaleForm({
              ...saleForm,
              sellerId: v
            })} error={saleErrors.sellerId} searchable placeholder="Seleccionar vendedor..." data-id="element-3258" />
              <Input type="date" label="Fecha" value={saleForm.date} onChange={e => setSaleForm({
              ...saleForm,
              date: e.target.value
            })} error={saleErrors.date} data-id="element-3259" />
            </div>
          </FormSection>

          <FormSection title="Productos" data-id="element-3260">
            {saleErrors.items && <p className="text-sm text-red-500 mb-2" data-id="element-3261">{saleErrors.items}</p>}
            <div className="space-y-3" data-id="element-3262">
              {saleItems.map((item, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3263">
                  <div className="flex-1" data-id="element-3264">
                    <Select options={products.filter(p => p.status === 'active').map(p => ({
                  value: p.id,
                  label: `${p.name} (${p.stock} disp.) - ${formatCurrency(p.price)}`
                }))} value={item.productId} onChange={v => updateSaleItem(idx, 'productId', v)} searchable placeholder="Seleccionar producto..." data-id="element-3265" />
                    {saleErrors[`item_${idx}`] && <p className="text-xs text-red-500 mt-1" data-id="element-3266">
                        {saleErrors[`item_${idx}`]}
                      </p>}
                  </div>
                  <div className="w-24" data-id="element-3267">
                    <Input type="number" min="1" value={item.quantity.toString()} onChange={e => updateSaleItem(idx, 'quantity', parseInt(e.target.value) || 1)} data-id="element-3268" />
                  </div>
                  <button type="button" onClick={() => removeSaleItem(idx)} className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg" disabled={saleItems.length === 1} data-id="element-3269">
                    <TrashIcon className="w-5 h-5" data-id="element-3270" />
                  </button>
                </div>)}
              <Button type="button" variant="outline" size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3272" />} onClick={addSaleItem} data-id="element-3271">
                Agregar otro producto
              </Button>
            </div>
            <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-between border border-orange-100 dark:border-orange-800" data-id="element-3273">
              <span className="text-orange-800 dark:text-orange-300 font-medium" data-id="element-3274">
                Total de la Venta:
              </span>
              <span className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-3275">
                {formatCurrency(calculateSaleTotal())}
              </span>
            </div>
          </FormSection>

          <FormSection title="Notas Adicionales" data-id="element-3276">
            <Textarea value={saleForm.notes} onChange={e => setSaleForm({
            ...saleForm,
            notes: e.target.value
          })} rows={2} placeholder="Observaciones sobre la venta..." data-id="element-3277" />
          </FormSection>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-3278">
            <Button variant="outline" type="button" onClick={() => setIsSaleModalOpen(false)} data-id="element-3279">
              Cancelar
            </Button>
            <Button type="submit" leftIcon={<DollarSignIcon className="w-4 h-4" data-id="element-3281" />} data-id="element-3280">
              Registrar Venta
            </Button>
          </div>
        </form>
      </Modal>
    </div>;
}